// SearchList.jsx
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BsPersonWorkspace } from "react-icons/bs";
import { FaRegUserCircle } from "react-icons/fa";
import { MdOutlineKeyboardDoubleArrowRight, MdSearch } from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { MdClose } from "react-icons/md";

import DynamicBreadcrumbs from 'components/breadcrumbs/dynamicbreadcrumbs';
import CommonModal from 'components/common/modal/DynamicModal';
import CustomToast from 'components/toast/customToast';
import Cmn_Workspace from 'components/cmn_workspace/cmn_workspace';
import SaveWorkspaceModal from 'components/authentication/signup-login/SaveWorkspaceModal';
import AuthModalHandler from 'components/authentication/signup-login/AuthModalHandler';
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import { useAutoLoadDefaultWorkspace } from 'components/authentication/signup-login/useAutoLoadDefaultWorkspace';

import { fetchSectorById } from '../../../store/sectorSlice';
import { clearIndicators, clearSectorIndicators } from '../../../store/filterSlice';
import { addIndicator, setWorkspaceLoaded, startNewWorkspace } from "store/workspaceSlice";
import { MdInfoOutline } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import IndicatorsList from '../Indicator/Indicator';

import { BASE_URL } from 'commonConfig/ApiConfig';

const SearchList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const subsector = location.state?.subsector;   // subsector object
  const subsubsector = location.state?.subsubsector;
  const indicatorData = location.state?.results?.indicators || [];
  const sectorFilter = location.state?.results.filter_metadata
  const initialSearchTerm = location.state?.searched || '';
  // console.log(sectorFilter)
  const { selectedSector: sector, loading } = useSelector((state) => state.sector);
  const selectedIndicators = useSelector(state => state.workspace.selectedIndicators);
  const selectedMapFeatures = useSelector(state => state.workspace.selectedMapFeatures || []);
  const isWorkspaceLoaded = useSelector(state => state.workspace.isWorkspaceLoaded);
  const loadedWorkspaceName = useSelector(state => state.workspace.loadedWorkspaceName);
  const hasUnsavedChanges = useSelector(state => state.workspace.hasUnsavedChanges);
  const currentWorkspace = useSelector(state => state.workspace.currentWorkspace);

  const { isAuthenticated, login } = useAuth();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSaveMode, setPendingSaveMode] = useState(null);
  const [modalMode, setModalMode] = useState('save');

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [indicatorDatas, setIndicatorData] = useState([]);
  const [filterMetaData, setMetaData] = useState([]);
  const [show, setShowModal] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showIndicatorCard, setShowIndicatorCard] = useState(false);
  
  // derive a simple localSelectedLocations list to pass into SaveWorkspaceModal
  const localSelectedLocations = Array.isArray(selectedMapFeatures) && selectedMapFeatures.length > 0
    ? selectedMapFeatures.map(f => ({
        value: f.properties?.code ?? f.id ?? null,
        label: f.name ?? f.properties?.name ?? '',
        geo_level: f.geo_level ?? f.properties?.geo_level_code ?? null,
      }))
    : [];

  useAutoLoadDefaultWorkspace();

  // fetch sector details (for breadcrumb etc.)
  useEffect(() => {
    if (subsector?.sector_id) {
      dispatch(fetchSectorById(subsector.sector_id));
    }
  }, [subsector, dispatch]);

  // set indicators from location/state
  useEffect(() => {
    dispatch(clearIndicators());
    dispatch(clearSectorIndicators());
    if (indicatorData) {  
      setIndicatorData(indicatorData);
      setShowIndicatorCard(true);  
    }
  }, [indicatorData, subsector?.code, dispatch]);

  // Add indicator to workspace
  const handleIndicatorSelect = (indicator) => {
    const exists = selectedIndicators.some(
      (i) => i.indicator_code === indicator.indicator_code
    );
    if (exists) {
      toast(<CustomToast message="Already added to workspace" type="info" />);
      return;
    }
    dispatch(addIndicator(indicator));
    toast(<CustomToast message={`${indicator.indicator_name} added to workspace`} type="success" />, { position: "top-right" });
  };

  // Visualize
  const visualize = (indicators) => {
    navigate('/mapconfig', { state: { selectedIndicators: indicators } });
  };

  // Handle search API call
 const handleSearch = async () => {
  if (!searchTerm.trim()) {
    toast(<CustomToast message="Please enter a search term" type="warning" />);
    return;
  }
  try {
    setSearchLoading(true);
    const payload = { searchText: searchTerm.trim() };
    const response = await axios.post(`${BASE_URL}indicators/searchAll`, payload);

    if (response.data?.indicators) {
      setIndicatorData(response.data.indicators);
      toast(<CustomToast message="Search completed" type="success" />, { position: "top-right" });
    } else {
      setIndicatorData([]);
      toast(<CustomToast message="No indicators found" type="info" />, { position: "top-right" });
    }

    setShowIndicatorCard(true);  // ✅ always show card after search, even if empty
  } catch (error) {
    toast(<CustomToast message="Search failed. Try again." type="error" />, { position: "top-right" });
  } finally {
    setSearchLoading(false);
  }
};

      const handleClearSearch = () => {
        setSearchTerm('');           
        setIndicatorData([]);        
        setShowIndicatorCard(false);
      };

  // Workspace management functions
  const handleSaveClick = (mode = 'save') => {
    if (!isAuthenticated) {
        setPendingSaveMode(mode);
        setShowAuthModal(true);
        return;
    }
    
    if (selectedIndicators.length === 0) {
        return;
    }
    
    setModalMode(mode);
    setShowSaveModal(true);
  };

  const handleLoginSuccess = (response) => {
    const loginData = response.data || response;
    let userData = null;
    let token = null;
    
    if (loginData && loginData.user) {
        userData = loginData.user;
        token = loginData.token;
    } else {
        userData = {
            name: response.name || response.user?.name || 'User',
            email: response.email || response.user?.email || 'user@example.com'
        };
        token = response.token || response.access_token;
    }
    
    login(userData, token);
    setShowAuthModal(false);
    
    if (pendingSaveMode) {
        setTimeout(() => {
            // Double-check authentication before opening save modal
            const token = localStorage.getItem('authToken');
            const userData = localStorage.getItem('userData');
            
            if (token && userData) {
                setModalMode(pendingSaveMode);
                setShowSaveModal(true);
            } else {
                // If still not authenticated, show error
            }
            setPendingSaveMode(null);
        }, 1000); // Increased delay to ensure auth state is fully updated
    }
  };

  const handleSaveSuccess = (workspaceData) => {
    // Always load the saved workspace as current (for both new saves and save as)
    if (workspaceData.id && workspaceData.name) {
      console.log('Loading saved workspace:', workspaceData.name, 'ID:', workspaceData.id);
      // Add a small delay to ensure the workspace is properly created
      setTimeout(() => {
        // Dispatch action to load the workspace
        dispatch(setWorkspaceLoaded({
          id: workspaceData.id,
          name: workspaceData.name,
          workspace: workspaceData
        }));
        console.log('Workspace loaded with name:', workspaceData.name);
      }, 100);
    }
    
    setShowSaveModal(false);
  };

  const handleNewWorkspace = () => {
    // Clear all indicators and locations and reset to "My Workspace" state
    dispatch(startNewWorkspace());
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <>
      {/* Toggle workspace button */}
      <button
        className="btn d-flex align-items-center ui-wp rotated-btn" style={{ backgroundColor: '#a3cefa' }}
        onClick={() => setShowWorkspace(!showWorkspace)}
      >
        <BsPersonWorkspace size={20} className="me-1" />
        Workspace
      </button>

      <div className="container-fluid bg-white py-4">
        <div className="row">
          {/* Indicators side */}
          <div className={`${!showWorkspace ? 'col-md-9' : 'col-md-12'}`}>
            {/* <DynamicBreadcrumbs /> */}
            <h5 className='mb-2'>Search Details </h5>
            {/* Search bar inside page */}
            <div className='col-md-5'>
              <div className="mb-3">
                <div style={{ display: "flex", alignItems: "center" }}>
  <div style={{ position: "relative", flex: 1 }}>
    <FormControl
      placeholder="Search Indicators"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{ paddingRight: "30px" }} // room for X icon
    />

    {(searchTerm || indicatorDatas.length > 0) && (
      <MdClose
        size={18}
        onClick={handleClearSearch}
        title="Clear Search"
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "pointer",
          color: "#6c757d",
        }}
      />
    )}
  </div>

  <Button
    variant="primary"
    onClick={handleSearch}
    disabled={searchLoading}
    className="ms-2"
  >
    {searchLoading ? "Searching..." : <MdSearch size={20} />}
  </Button>
</div>


              </div>
            </div>

            {/* Indicators list */}
            <div className="row">
            {showIndicatorCard && (
                  <div className="row">
                    <div className="row">
                  <IndicatorsList
                    indicatorData={indicatorDatas}
                    filterMetaData={sectorFilter}
                    onIndicatorSelect={handleIndicatorSelect}
                    selectedIndicators={selectedIndicators}
                  />

                  {/* Show message inside same card when no indicators found */}
                
                </div>

                  </div>
                )}
            </div>
          </div>

          {/* Workspace side panel */}
          {!showWorkspace && (
            <div
              className="position-fixed end-0 bg-white workspace-scroll shadow"
              style={{
                top: '68px',
                width: '420px',
                height: '100vh',
                zIndex: 1050,
                overflowY: 'scroll',
                padding: '0',
                borderLeft: '1px solid #ddd',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              <div className="h-100 border-0">
                <div className="card-header ui-card-header d-flex justify-content-between align-items-center border-0">
                  <h5 className="mb-0">
                    <FaRegUserCircle /> &nbsp;
                    {isWorkspaceLoaded ? loadedWorkspaceName : 'My Workspace'}
                    {hasUnsavedChanges && <span className="text-warning ms-2">*</span>}&nbsp;
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="filter-tooltip">
                          <p>Click save to access your work later.</p>
                        </Tooltip>
                      }
                    >
                      <span>
                        <MdInfoOutline style={{ cursor: "pointer", color: "#000000" }} />
                      </span>
                    </OverlayTrigger>
                  </h5>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => setShowWorkspace(true)}
                  >
                    <MdOutlineKeyboardDoubleArrowRight />
                  </button>
                </div>
                <div className="card-body pt-3 p-2">
                  <div className='row'>
                    <div className='col-md-4'><h5 className='f-14 fw-bold'>Indicators</h5></div>
                    <div className='col-md-8'>
                      <div className='d-flex ui-up'>
                        <div className='p-1'>
                          <Button 
                            variant="primary"
                            className="f-14 btn-sm" 
                            onClick={() => handleSaveClick(isWorkspaceLoaded ? 'update' : 'save')}
                          >
                            <i className={`bi ${isWorkspaceLoaded ? 'bi-arrow-repeat' : 'bi-save'} me-1`}></i>
                            {isWorkspaceLoaded ? 'Update' : 'Save'}
                          </Button>
                        </div>
                        <div className="p-1">
                          <Button variant="outline-primary" className="btn-sm f-14" onClick={() => handleSaveClick('saveAs')}>
                            Save As
                          </Button>
                        </div>
                        <div className="p-1">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={handleNewWorkspace}
                          >
                            <i className="bi bi-plus-circle me-1"></i> New
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Cmn_Workspace visualize={(inds) => {visualize(inds)} }
                showWorkspace={showWorkspace} 
                setShowWorkspace={setShowWorkspace}/>
                </div>
              </div>
            </div>
          )}

          {/* Common Modal */}
          <CommonModal
            isOpen={show}
            onClose={() => setShowModal(false)}
            title="Modal Title"
            footer={
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
                <button className="btn btn-primary">Save changes</button>
              </>
            }
          >
            <p>This is a dynamic modal body</p>
          </CommonModal>
        </div>
      </div>

      <SaveWorkspaceModal
        show={showSaveModal}
        handleClose={() => setShowSaveModal(false)}
        onSave={handleSaveSuccess}
        selectedIndicators={selectedIndicators}
        selectedLocations={localSelectedLocations} 
        allMapFeatures={selectedMapFeatures}
        mode={modalMode}
      />
      <AuthModalHandler
        show={showAuthModal}
        handleClose={() => {
          setShowAuthModal(false);
          setPendingSaveMode(null);
        }}
        initialMode="login"
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default SearchList;
